import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  Fade,
  Pagination,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  TrendingUp as CreditIcon,
  TrendingDown as DebitIcon,
  Undo as RefundIcon,
  Payment as PaymentIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const TransactionList = ({ transactions = [] }) => {
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calcular paginación
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const getTransactionIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'credit':
        return <CreditIcon sx={{ color: '#4CAF50' }} />;
      case 'debit':
        return <DebitIcon sx={{ color: '#F44336' }} />;
      case 'refund':
        return <RefundIcon sx={{ color: '#FF9800' }} />;
      case 'payment':
        return <PaymentIcon sx={{ color: '#2196F3' }} />;
      default:
        return <ReceiptIcon sx={{ color: '#75787B' }} />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'credit':
      case 'refund':
        return {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32',
          borderColor: '#4CAF50'
        };
      case 'debit':
      case 'payment':
        return {
          backgroundColor: '#FFEBEE',
          color: '#C62828',
          borderColor: '#F44336'
        };
      default:
        return {
          backgroundColor: '#F5F5F5',
          color: '#75787B',
          borderColor: '#BDBDBD'
        };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: 'short' 
      }),
      time: date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    };
  };

  const formatAmount = (amount, type) => {
    const isPositive = type === 'credit' || type === 'refund';
    const sign = isPositive ? '+' : '-';
    return `${sign}$${Math.abs(Number(amount)).toFixed(2)}`;
  };

  const getTransactionTitle = (type) => {
    switch (type?.toLowerCase()) {
      case 'credit':
        return 'Recarga de Saldo';
      case 'debit':
        return 'Pago Realizado';
      case 'refund':
        return 'Reembolso';
      case 'payment':
        return 'Pago de Servicio';
      default:
        return 'Transacción';
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 38, 66, 0.1)',
                mr: 2
              }}
            >
              <HistoryIcon sx={{ color: '#002642', fontSize: '1.5rem' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#002642',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              Historial de Transacciones
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <ReceiptIcon 
              sx={{ 
                fontSize: { xs: '3rem', sm: '4rem' }, 
                color: '#E0E0E0', 
                mb: 2 
              }} 
            />
            <Typography
              variant="h6"
              sx={{
                color: '#75787B',
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              No hay transacciones
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#A0A0A0',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Cuando realices transacciones aparecerán aquí
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 38, 66, 0.1)',
              mr: 2
            }}
          >
            <HistoryIcon sx={{ color: '#002642', fontSize: '1.5rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#002642',
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              Historial de Transacciones
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#75787B',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {transactions.length} transacción{transactions.length !== 1 ? 'es' : ''} encontrada{transactions.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Transaction Cards */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {paginatedTransactions.map((transaction, index) => {
            const { date, time } = formatDate(transaction.created_at);
            const colorConfig = getTransactionColor(transaction.type);
            
            return (
              <Grid item xs={12} key={transaction.bm_id || index}>
                <Fade in={true} timeout={300 + index * 100}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${colorConfig.borderColor}`,
                      backgroundColor: colorConfig.backgroundColor,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-2px)' },
                        boxShadow: 3
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: { xs: 'flex-start', sm: 'center' }, 
                        gap: { xs: 1.5, sm: 2 },
                        flexDirection: { xs: 'row', sm: 'row' }
                      }}>
                        {/* Transaction Icon */}
                        <Avatar
                          sx={{
                            width: { xs: 40, sm: 56 },
                            height: { xs: 40, sm: 56 },
                            backgroundColor: '#FFFFFF',
                            border: `2px solid ${colorConfig.borderColor}`,
                            flexShrink: 0
                          }}
                        >
                          {getTransactionIcon(transaction.type)}
                        </Avatar>

                        {/* Transaction Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: { xs: 'flex-start', sm: 'flex-start' }, 
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 0.5, sm: 2 }
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                flexDirection: { xs: 'column', sm: 'row' },
                                mb: { xs: 0.5, sm: 1 }
                              }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: colorConfig.color,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    mb: { xs: 0.25, sm: 0 }
                                  }}
                                >
                                  {getTransactionTitle(transaction.type)}
                                </Typography>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    color: colorConfig.color,
                                    fontSize: { xs: '1.1rem', sm: '1.5rem' },
                                    lineHeight: 1
                                  }}
                                >
                                  {formatAmount(transaction.amount, transaction.type)}
                                </Typography>
                              </Box>
                              
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#75787B',
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  mb: { xs: 0.75, sm: 1 },
                                  lineHeight: 1.3
                                }}
                              >
                                {transaction.description || 'Sin descripción'}
                              </Typography>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: { xs: 0.5, sm: 1 }, 
                                flexWrap: 'wrap' 
                              }}>
                                <Chip
                                  label={transaction.type?.toUpperCase() || 'N/A'}
                                  size="small"
                                  sx={{
                                    backgroundColor: colorConfig.borderColor,
                                    color: '#FFFFFF',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                    height: { xs: 20, sm: 28 }
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#75787B',
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                                  }}
                                >
                                  {date} • {time}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: { xs: 3, sm: 4 }
          }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "medium" : "large"}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 1 : 2}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  minWidth: isMobile ? 32 : 40,
                  height: isMobile ? 32 : 40,
                  mx: isMobile ? 0.25 : 0.5,
                  '&.Mui-selected': {
                    backgroundColor: '#002642',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#00577A'
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 38, 66, 0.1)'
                  }
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionList;